import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import { Order } from '../../../services/orderService';
import { PaymentProviders } from '../CheckoutPage';
import { useTranslation } from '../../../i18n';
import api from '../../../services/api';

interface PaymentStepProps {
    order: Order;
    paymentProviders: PaymentProviders | null;
    isCartFree: boolean;
    loading: boolean;
    error: string | null;
    onBack: () => void;
    onFreeEnrollment: () => void;
    onPaymentComplete: (order: Order) => void;
}

type PaymentMethod = 'free' | 'paypal' | 'stripe';

const PaymentStep: React.FC<PaymentStepProps> = ({
    order,
    paymentProviders,
    isCartFree,
    loading,
    error,
    onBack,
    onFreeEnrollment,
    onPaymentComplete,
}) => {
    const { t } = useTranslation();
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
        isCartFree ? 'free' : null
    );
    const [processing, setProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);

    const availableProviders = paymentProviders?.providers.filter(p => p.enabled) || [];
    const hasPayPal = availableProviders.some(p => p.id === 'paypal');
    const hasStripe = availableProviders.some(p => p.id === 'stripe');

    const handlePayPalPayment = async () => {
        setProcessing(true);
        setPaymentError(null);

        try {
            // Create PayPal order
            const createResponse = await api.post<{
                success: boolean;
                paypalOrderId: string;
                paymentId: number;
            }>(`/payments/paypal/create/${order.id}`);

            if (!createResponse.data.success) {
                throw new Error('Failed to create PayPal order');
            }

            const paypalOrderId = createResponse.data.paypalOrderId;

            // In production, this would open PayPal popup/redirect
            // For now, we'll simulate the approval flow
            // The PayPal SDK would normally handle this
            const approvalUrl = paymentProviders?.paypalClientId
                ? `https://www.sandbox.paypal.com/checkoutnow?token=${paypalOrderId}`
                : null;

            if (approvalUrl) {
                // Open PayPal in new window
                const popup = window.open(approvalUrl, 'paypal', 'width=500,height=600');

                // Poll for completion (in production, use PayPal SDK callbacks)
                const checkPayment = setInterval(async () => {
                    if (popup?.closed) {
                        clearInterval(checkPayment);

                        // Try to capture the payment
                        try {
                            const captureResponse = await api.post<{
                                success: boolean;
                                status: string;
                                order: Order;
                            }>(`/payments/paypal/capture/${order.id}`, {
                                paypalOrderId,
                            });

                            if (captureResponse.data.success) {
                                onPaymentComplete(captureResponse.data.order);
                            } else {
                                setPaymentError('Zahlung wurde nicht abgeschlossen');
                            }
                        } catch {
                            setPaymentError('Fehler bei der Zahlungsverarbeitung');
                        }

                        setProcessing(false);
                    }
                }, 1000);

                // Timeout after 10 minutes
                setTimeout(() => {
                    clearInterval(checkPayment);
                    if (processing) {
                        setProcessing(false);
                        setPaymentError('Zeitlimit ueberschritten');
                    }
                }, 600000);
            } else {
                throw new Error('PayPal not configured');
            }
        } catch (err) {
            console.error('PayPal payment error:', err);
            setPaymentError(t('checkout.errors.paymentFailed'));
            setProcessing(false);
        }
    };

    const handleStripePayment = async () => {
        setProcessing(true);
        setPaymentError(null);

        try {
            // Create Stripe payment intent
            const response = await api.post<{
                success: boolean;
                clientSecret: string;
                paymentIntentId: string;
            }>(`/payments/stripe/create-intent/${order.id}`);

            if (!response.data.success) {
                throw new Error('Failed to create payment intent');
            }

            // In production, use Stripe Elements here
            // For now, redirect to Stripe Checkout or use embedded form
            setPaymentError('Stripe-Integration wird noch implementiert');
            setProcessing(false);

        } catch (err) {
            console.error('Stripe payment error:', err);
            setPaymentError(t('checkout.errors.paymentFailed'));
            setProcessing(false);
        }
    };

    const handlePayment = () => {
        switch (selectedMethod) {
            case 'free':
                onFreeEnrollment();
                break;
            case 'paypal':
                handlePayPalPayment();
                break;
            case 'stripe':
                handleStripePayment();
                break;
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('checkout.payment.title')}
            </Typography>

            {(error || paymentError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || paymentError}
                </Alert>
            )}

            {isCartFree ? (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Diese Bestellung ist kostenlos! Klicken Sie auf "Jetzt starten", um sofort Zugang zu erhalten.
                </Alert>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Waehlen Sie Ihre bevorzugte Zahlungsmethode
                </Typography>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {isCartFree && paymentProviders?.allowFreeEnrollment && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                            variant={selectedMethod === 'free' ? 'outlined' : 'elevation'}
                            sx={{
                                borderColor: selectedMethod === 'free' ? 'primary.main' : undefined,
                                borderWidth: selectedMethod === 'free' ? 2 : 1,
                            }}
                        >
                            <CardActionArea onClick={() => setSelectedMethod('free')}>
                                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                    <CheckCircleIcon
                                        sx={{ fontSize: 48, color: 'success.main', mb: 1 }}
                                    />
                                    <Typography variant="h6">
                                        Kostenlos
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Sofortiger Zugang
                                    </Typography>
                                    {selectedMethod === 'free' && (
                                        <Chip
                                            label="Ausgewaehlt"
                                            color="primary"
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                )}

                {!isCartFree && hasPayPal && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                            variant={selectedMethod === 'paypal' ? 'outlined' : 'elevation'}
                            sx={{
                                borderColor: selectedMethod === 'paypal' ? 'primary.main' : undefined,
                                borderWidth: selectedMethod === 'paypal' ? 2 : 1,
                            }}
                        >
                            <CardActionArea onClick={() => setSelectedMethod('paypal')}>
                                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                    <Box
                                        component="img"
                                        src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png"
                                        alt="PayPal"
                                        sx={{ height: 40, mb: 1 }}
                                    />
                                    <Typography variant="h6">
                                        PayPal
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Sicher und schnell bezahlen
                                    </Typography>
                                    {selectedMethod === 'paypal' && (
                                        <Chip
                                            label="Ausgewaehlt"
                                            color="primary"
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                )}

                {!isCartFree && hasStripe && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                            variant={selectedMethod === 'stripe' ? 'outlined' : 'elevation'}
                            sx={{
                                borderColor: selectedMethod === 'stripe' ? 'primary.main' : undefined,
                                borderWidth: selectedMethod === 'stripe' ? 2 : 1,
                            }}
                        >
                            <CardActionArea onClick={() => setSelectedMethod('stripe')}>
                                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                    <LockIcon
                                        sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
                                    />
                                    <Typography variant="h6">
                                        Kreditkarte
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Visa, Mastercard, AMEX
                                    </Typography>
                                    {selectedMethod === 'stripe' && (
                                        <Chip
                                            label="Ausgewaehlt"
                                            color="primary"
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {!isCartFree && availableProviders.length === 0 && (
                <Alert severity="warning">
                    Keine Zahlungsmethoden verfuegbar. Bitte kontaktieren Sie den Support.
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    disabled={loading || processing}
                >
                    {t('ra.action.back')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handlePayment}
                    disabled={!selectedMethod || loading || processing}
                    startIcon={processing ? <CircularProgress size={20} /> : undefined}
                >
                    {isCartFree ? 'Jetzt starten' : 'Jetzt bezahlen'}
                </Button>
            </Box>
        </Box>
    );
};

export default PaymentStep;
